import React, { useState } from "react"
import { SearchState } from "../../../types"
import {
  isCategoryActive,
  selectProductCategory,
  unselectProductCategory,
} from "../../../utils/filters"
import {
  CategoriesTree,
  CategoriesTreeItem,
} from "../../controller/state/categories"
import ProductCheckboxFilterItem from "../ProductCheckboxFilterItem"

interface Props {
  tree: CategoriesTree
  searchParams: SearchState
  updateSearchParams: (
    transform: (currentState: SearchState) => SearchState
  ) => void
  className?: string
  maxScrollItems?: number
  defaultExpandedOptions?: CategoriesTreeItem[]
}

type ExpansionMap = { [key: string]: boolean }

const createDefaultExpendedMap = (
  defaultExpandedOptions: CategoriesTreeItem[]
): ExpansionMap => {
  const map: ExpansionMap = {}
  defaultExpandedOptions.forEach((x) => (map[x.code] = true))
  return map
}

const getDefaultExpandedNodes = (
  node: CategoriesTreeItem
): CategoriesTreeItem[] => {
  const expandedNodes: CategoriesTreeItem[] = []
  for (const children of node.children) {
    const expendedChildren = getDefaultExpandedNodes(children)
    expandedNodes.push(...expendedChildren)
  }
  if (expandedNodes.length > 0 || node.checked) {
    expandedNodes.push(node)
  }
  return expandedNodes
}

const getDefaultExpandedMap = (tree: CategoriesTree) => {
  try {
    const exp = []
    for (const root of tree.root) {
      const expandedChildren = getDefaultExpandedNodes(root)
      exp.push(...expandedChildren)
    }

    return createDefaultExpendedMap(exp)
  } catch (e) {
    console.error("Error creating default expansion map", e, tree)
    return {}
  }
}

const ProductCategoryTreeFilter = ({
  tree,
  searchParams,
  updateSearchParams,
  className,
  maxScrollItems,
}: Props) => {
  const [expandedMap, setExpandedMap] = useState<ExpansionMap>(
    getDefaultExpandedMap(tree)
  )

  const setOptionExpanded = (option: CategoriesTreeItem, value: boolean) =>
    setExpandedMap({
      ...expandedMap,
      [option.code]: value,
    })

  const setOptionsExpanded = (codes: string[], value: boolean) => {
    const newMap = {
      ...expandedMap,
    }
    for (const code of codes) {
      newMap[code] = value
    }
    setExpandedMap(newMap)
  }

  const selectOption = (option: CategoriesTreeItem) => {
    setOptionsExpanded(option.allCodes, true)
    console.log("opt", option)
    updateSearchParams((state) => {
      return {
        ...state,
        categoryCodes: selectProductCategory(searchParams, option),
      }
    })
  }

  const unselectOption = (option: CategoriesTreeItem) => {
    setOptionExpanded(option, false)
    updateSearchParams((state) => {
      return {
        ...state,
        categoryCodes: unselectProductCategory(searchParams, option, tree),
      }
    })
  }

  const toggleOption = (option: CategoriesTreeItem) => {
    if (!isCategoryActive(option)) {
      console.log("select", option)
      selectOption(option)
    } else {
      console.log("unselect", option)
      unselectOption(option)
    }
  }

  const handleExpand = (option: CategoriesTreeItem) => {
    setOptionExpanded(option, !expandedMap[option.code])
  }

  return (
    <div className={className}>
      <div
        style={{
          maxHeight: maxScrollItems ? maxScrollItems * 36 : undefined,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <ProductCheckboxFilterItem
          expandedMap={expandedMap}
          onExpand={handleExpand}
          options={tree.root}
          onClick={toggleOption}
          firstLevel={true}
          expanded
        />
      </div>
    </div>
  )
}

export default ProductCategoryTreeFilter
